import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';

import {
  BatteryCardsSectionDocument,
  BatteryCardsSectionQuery,
  BatteryCardsSectionQueryVariables,
  BatteryHeroDocument,
  BatteryHeroQuery,
  BatteryHeroQueryVariables,
  BatteryPartnersBlogSectionDocument,
  BatteryPartnersBlogSectionQuery,
  BatteryPartnersBlogSectionQueryVariables,
  BatteryProductImageBlockDocument,
  BatteryProductImageBlockQuery,
  BatteryProductImageBlockQueryVariables,
  BatterySeoDocument,
  BatterySeoQuery,
  BatterySeoQueryVariables,
  BatteryTwoImagesGridDocument,
  BatteryTwoImagesGridQuery,
  BatteryTwoImagesGridQueryVariables,
  BatteryVideoSectionDocument,
  BatteryVideoSectionQuery,
  BatteryVideoSectionQueryVariables,
  FooterDocument,
  FooterQuery,
  FooterQueryVariables,
  FormDocument,
  FormQuery,
  FormQueryVariables,
  NavigationDocument,
  NavigationQuery,
  NavigationQueryVariables,
} from '@/api';
import { addApolloState, initializeApollo } from '@/api/apolloClient';
import { Layout } from '@/components/layout/Layout';
import { Partners } from '@/components/partners/Partners';
import { Seo } from '@/components/Seo';
import EnhancedProductImageBlock from '@/components/shared/EnhancedProductImageBlock';
import HeroVideoSection from '@/components/shared/HeroVideoSection';
import IconCards from '@/components/shared/IconCards';
import TwoImagesGrid from '@/components/shared/TwoImagesGrid';
import EnhancedVideoSection from '@/components/shared/VideoSection';

export const getStaticProps: GetStaticProps = async () => {
  const client = initializeApollo();

  // prefetch
  await client.query({ query: BatteryHeroDocument });
  await client.query({ query: BatteryCardsSectionDocument });
  await client.query({ query: BatteryProductImageBlockDocument });
  await client.query({ query: BatteryTwoImagesGridDocument });
  await client.query({ query: BatteryVideoSectionDocument });
  await client.query({ query: BatterySeoDocument });
  await client.query({ query: NavigationDocument });
  await client.query({ query: FormDocument });
  await client.query({ query: FooterDocument });
  await client.query({ query: BatteryPartnersBlogSectionDocument });

  const { data: heroData } = await client.query<
    BatteryHeroQuery,
    BatteryHeroQueryVariables
  >({
    query: BatteryHeroDocument,
  });

  const { data: cardsData } = await client.query<
    BatteryCardsSectionQuery,
    BatteryCardsSectionQueryVariables
  >({
    query: BatteryCardsSectionDocument,
  });

  const { data: productImageBlockData } = await client.query<
    BatteryProductImageBlockQuery,
    BatteryProductImageBlockQueryVariables
  >({
    query: BatteryProductImageBlockDocument,
  });

  const { data: twoImagesGridData } = await client.query<
    BatteryTwoImagesGridQuery,
    BatteryTwoImagesGridQueryVariables
  >({
    query: BatteryTwoImagesGridDocument,
  });

  const { data: videoSectionData } = await client.query<
    BatteryVideoSectionQuery,
    BatteryVideoSectionQueryVariables
  >({
    query: BatteryVideoSectionDocument,
  });

  const { data: seoData } = await client.query<
    BatterySeoQuery,
    BatterySeoQueryVariables
  >({ query: BatterySeoDocument });

  const { data: headerData } = await client.query<
    NavigationQuery,
    NavigationQueryVariables
  >({ query: NavigationDocument });

  const { data: formData } = await client.query<FormQuery, FormQueryVariables>({
    query: FormDocument,
  });

  const { data: footerData } = await client.query<
    FooterQuery,
    FooterQueryVariables
  >({ query: FooterDocument });

  const { data: partnersBlogSectionData } = await client.query<
    BatteryPartnersBlogSectionQuery,
    BatteryPartnersBlogSectionQueryVariables
  >({ query: BatteryPartnersBlogSectionDocument });

  return addApolloState(client, {
    props: {
      heroData,
      cardsData,
      productImageBlockData,
      twoImagesGridData,
      videoSectionData,
      seoData,
      formData,
      footerData,
      headerData,
      partnersBlogSectionData,
    },
  });
};

const Battery: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  heroData,
  cardsData,
  productImageBlockData,
  twoImagesGridData,
  videoSectionData,
  seoData,
  formData,
  footerData,
  headerData,
  partnersBlogSectionData,
}) => (
  <Layout formData={formData} footerData={footerData} headerData={headerData}>
    <Seo seo={seoData?.battery?.data?.attributes?.seo} />
    <HeroVideoSection
      hero={heroData?.battery?.data?.attributes?.heroVideoSection}
      subtitleMaxWidth={540}
    />
    <IconCards
      cards={cardsData?.battery?.data?.attributes?.cardsSection}
      titleMaxWidth={830}
    />
    <EnhancedProductImageBlock
      productImageBlock={
        productImageBlockData?.battery?.data?.attributes?.productImageBlock
      }
      parentIndex={1}
    />
    <TwoImagesGrid
      twoImagesGrid={
        twoImagesGridData?.battery?.data?.attributes?.twoImagesGrid
      }
      descriptionMaxWidth={450}
    />
    <EnhancedVideoSection
      videoSection={videoSectionData?.battery?.data?.attributes?.videoSection}
      descriptionMaxWidth={740}
    />
    <Partners
      data={
        partnersBlogSectionData?.battery?.data?.attributes?.partnersBlogSection
      }
    />
  </Layout>
);

export default Battery;
